import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, NavLink } from "react-router-dom";
import {
  getPartnerById,
  updatePartnerStatus,
  getPrivateFileBlob,
} from "../Services/partnerService";
import Skeleton from "../Components/SkeletonLoader";
import { toast } from "react-toastify";
import { RoutesPaths } from "../Router/constant";
import ConfirmationModal from "../Components/ConfirmationModal";
import { contractFileUpload } from "../Services/bookingService";

export default function PartnersDetail() {
  const fileInputRef = useRef(null);
  const [isContractUploaded, setIsContractUploaded] = useState(false);
  const { id } = useParams();
  const [singlePartnerData, setSinglePartnerData] = useState(null);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loadingAction, setLoadingAction] = useState(null);
  const navigate = useNavigate();
  const [rejectReason, setRejectReason] = useState("");
  const [confirmation, setConfirmation] = useState({
    show: false,
    type: "", // "accept" or "reject"
  });

  useEffect(() => {
    if (id) fetchPartnerData();
  }, [id]);

  const handleViewClick = () => {
    navigate(RoutesPaths.FREIGHTAGREEMENT, {
      state: { singlePartnerData },
    });
  };

  const partnerId = singlePartnerData?._id;
  const handleView = async (filename) => {
    const newTab = window.open("", "_blank");

    try {
      const res = await getPrivateFileBlob(filename);

      const blob = new Blob([res.data], {
        type: res.headers?.["content-type"] || "application/pdf",
      });

      const url = URL.createObjectURL(blob);
      newTab.location.href = url;

      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      newTab?.close();
      alert("Unable to open document (token invalid or file missing).");
    }
  };

  const fetchPartnerData = async () => {
    try {
      const res = await getPartnerById(id);
      const user = res?.data?.data?.user;
      setSinglePartnerData(user);
      setServiceTypes(user?.serviceTypes);
    } catch (error) {
      console.error("Error fetching user:", error);
    }
  };

  const handleOpenAcceptModal = () => {
    setConfirmation({ show: true, type: "accept" });
  };

  const handleOpenRejectModal = () => {
    setRejectReason("");
    setConfirmation({ show: true, type: "reject" });
  };

  const handleCloseModal = () => {
    setConfirmation({ show: false, type: "" });
    setRejectReason("");
  };

  const handleConfirmAction = async () => {
    try {
      setLoadingAction(confirmation.type);
      const status = confirmation.type === "accept" ? "accepted" : "rejected";

      const res = await updatePartnerStatus(id, status, rejectReason);

      toast.success(res.data.message);
      await fetchPartnerData();
      // navigate(RoutesPaths.PARTNERS);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update vendor status.");
    } finally {
      setLoadingAction(null);
      handleCloseModal();
    }
  };

  //   useEffect(() => {
  //     if (singlePartnerData?.uploadPartnerContractPDF) {
  //       setIsContractUploaded(true);
  //     }
  //   }, [singlePartnerData]);

  const formatDocumentTitle = (key) => {
    const titleMap = {
      commercialInvoice: "Commercial Invoice",
      customsImportDeclaration: "Customs Import Declaration",
      declarationOfCustomsValue: "Declaration of Customs Value (ADV)",
      loadingCertificate: "Loading Certificate (ARC / Waiver / CNCA)",
      ministryOfIndustryLicense:
        "Ministry of Industry and Trade (MINCO) License",
      packingList: "Packing List",
      terminalHandlingReceipts: "Terminal Handling Receipts",
      solasCertificate: "SOLAS Certificate",
    };
    return titleMap[key] || key.replace(/([A-Z])/g, " $1").trim();
  };

  const documentEntries = singlePartnerData?.documents
    ? Object.entries(singlePartnerData.documents).map(([key, value]) => ({
        title: formatDocumentTitle(key),
        file: value,
        type: value?.split(".").pop()?.toUpperCase() || "N/A",
      }))
    : [];

  const [contractFile, setContractFile] = useState();
  const [contractLoader, setContractLoader] = useState(true);
  const [contractLoading, setContractLoading] = useState(false);

  const handleContractFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";

    if (!isImage && !isPdf) {
      toast.error("Only images or PDF files are allowed");
      return;
    }

    setContractFile(file);
    setIsContractUploaded(true); // <-- make bar green
    setContractLoader(false);
  };

  const handleContractSubmit = async () => {
    try {
      setContractLoading(true);
      const response = await contractFileUpload(partnerId, contractFile);
      toast.success(response?.data?.message);
      fetchPartnerData();
      setIsContractUploaded(false);
      setContractLoader(true);
    } catch (err) {
      console.error("File upload failed:", err);
      toast.error(err?.response?.data?.data);
      setContractLoader(true);
      setContractLoading(false);
    } finally {
      setContractLoading(false);
    }
  };

  const handleContractCancel = () => {
    setContractFile(" ");
    setIsContractUploaded(false); // <-- make bar green
    setContractLoader(true);
  };

  return (
    <>
      <div className="container-fluid partner-details-page">
        <div className="row">
          <div className="col-md-12 dashboardSection">
            <div className="logisticsMenu logisticsMenuBooking p-0">
              <p>
                <NavLink to={RoutesPaths.DASHBOARD}>
                  <img src="/assets/img/homeIcon.svg" alt="" />
                </NavLink>
              </p>
              <img src="/assets/img/chevron-right.svg" alt="" />
              <p>
                <NavLink
                  to={RoutesPaths.PARTNERS}
                  style={{ cursor: "pointer" }}
                >
                  Vendors
                </NavLink>
              </p>
              <img src="/assets/img/chevron-right.svg" alt="" />
              <p className="activeSegment">Vendor id : {id}</p>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12">
            <div className="partners-page">
              <div className="partners-bar">
                <div className="partners-container">
                  <h1>Vendor details</h1>
                </div>
              </div>

              <main>
                <section className="partners-card">
                  <div className="partners-head">
                    <div className="partners-identity">
                      <h2 className="partners-company">
                        {singlePartnerData?.companyName}
                      </h2>
                      <p className="partners-email">
                        {singlePartnerData?.email}
                      </p>
                    </div>
                  </div>

                  <form className="partners-grid">
                    <div className="partners-field">
                      <label>Contact person name</label>
                      <input
                        type="text"
                        readOnly
                        value={singlePartnerData?.contactPersonName || ""}
                      />
                    </div>

                    <div className="partners-field">
                      <label>Business registration number / Tax id</label>
                      <input
                        type="text"
                        readOnly
                        value={
                          singlePartnerData?.businessRegistrationNumber || ""
                        }
                      />
                    </div>
                    <div className="partners-field">
                      <label>Service types</label>
                      <input
                        type="text"
                        readOnly
                        value={singlePartnerData?.serviceTypes || ""}
                      />
                    </div>

                    <div className="partners-field">
                      <label>Headquarters address</label>
                      <input
                        type="text"
                        readOnly
                        value={
                          singlePartnerData?.headquartersAddress?.address || ""
                        }
                      />
                    </div>

                    <div className="partners-field">
                      <label>Phone number</label>
                      <input
                        type="text"
                        readOnly
                        value={`(${singlePartnerData?.countryCode || ""}) ${singlePartnerData?.phone || ""}`}
                      />
                    </div>

                    <div className="partners-field">
                      <label>Contact person title</label>
                      <input
                        type="text"
                        readOnly
                        value={singlePartnerData?.contactPersonTitle || ""}
                      />
                    </div>

                    <div className="partners-field">
                      <label>Website</label>
                      <input
                        type="text"
                        readOnly
                        value={singlePartnerData?.website || ""}
                      />
                    </div>

                    <div className="partners-field">
                      <label>Number of employees</label>
                      <input
                        type="text"
                        readOnly
                        value={singlePartnerData?.numberOfEmployees || ""}
                      />
                    </div>
                    <div className="partners-field">
                      <label>Year of experience</label>
                      <input
                        type="text"
                        readOnly
                        value={singlePartnerData?.yearOfExperience || ""}
                      />
                    </div>
                    <div className="partners-field">
                      <label>Geographic coverage areas</label>
                      <input
                        type="text"
                        readOnly
                        value={singlePartnerData?.geographicCoverageAreas || ""}
                      />
                    </div>
                    <div className="partners-field">
                      <label>Fleet capacity</label>
                      <div>
                        <div className="fleet-inputs mb-2" style={{ gap: "0" }}>
                          <div className="col-4 p-0">Vehicle type</div>
                          <div className="col-4 p-0 pl-1">Qty</div>
                          <div className="col-4 p-0 pl-2">Capacity</div>
                        </div>

                        {(singlePartnerData?.fleetCapacity ?? []).length > 0 ? (
                          singlePartnerData.fleetCapacity.map((item, idx) => (
                            <div className="fleet-inputs mb-2" key={idx}>
                              <input
                                className="col-4"
                                type="text"
                                readOnly
                                value={item?.vehicleType ?? ""}
                              />
                              <input
                                className="col-4"
                                type="text"
                                readOnly
                                value={item?.qty ?? ""}
                              />
                              <input
                                className="col-4"
                                type="text"
                                readOnly
                                value={item?.capacity ?? ""}
                              />
                            </div>
                          ))
                        ) : (
                          <div className="fleet-empty">
                            No fleet capacity added
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="partners-field">
                      <label>Ware housing capacity</label>
                      <input
                        type="text"
                        readOnly
                        value={singlePartnerData?.warehousingCapacity || ""}
                      />
                    </div>
                  </form>

                  <div className="lc__container">
                    <h2 className="lc__title">Licensing &amp; compliance</h2>

                    <div className="lc__grid">
                      {documentEntries.length > 0 ? (
                        documentEntries.map((doc, i) => (
                          <div key={i} className="lc__card lc__cardRow">
                            <div className="lc__left">
                              <span className="lc__icon" aria-hidden>
                                <svg
                                  viewBox="0 0 24 24"
                                  width="22"
                                  height="22"
                                  fill="none"
                                  stroke="white"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M3 14v4a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3v-4" />
                                  <path d="M7 10l5 5 5-5" />
                                  <path d="M12 15V3" />
                                </svg>
                              </span>{" "}
                              <span className="lc__text">
                                <span className="lc__name">{doc.title}</span>
                                <span className="lc__meta">
                                  File Format: {doc.type}
                                </span>
                              </span>
                            </div>

                            <span
                              className="lc__viewBtn"
                              onClick={() => handleView(doc.file)}
                            >
                              View
                            </span>
                          </div>
                        ))
                      ) : (
                        <p>No documents uploaded.</p>
                      )}
                    </div>

                    {singlePartnerData?.status === "rejected" ? (
                      <div className="lc__actions">
                        <button
                          type="button"
                          className="lc__btn lc__btn--ghost"
                          onClick={() => navigate(RoutesPaths.PARTNERS)}
                        >
                          Back
                        </button>
                        <button
                          type="button"
                          className="lc__btn lc__btn--ghost"
                          disabled
                        >
                          Cancelled Request
                        </button>
                      </div>
                    ) : singlePartnerData?.status === "accepted" ? (
                      <>
                        <br />
                        <div className="contractManagementWrapper">
                          <h2 className="lc__title">Upload contract</h2>

                          {serviceTypes.length > 0 && (
                            <>
                              <div className="contractCardNew">
                                <div>
                                  <h5 className="cardTitle">
                                    <img
                                      src="/assets/img/featuredIcon.png"
                                      alt=""
                                    />
                                    <span style={{ marginLeft: "10px" }}>
                                      Contract from Quimera team :{" "}
                                    </span>
                                    <span
                                      className={
                                        singlePartnerData?.partnershipContract
                                          ?.status === "accepted"
                                          ? "text-success"
                                          : singlePartnerData
                                                ?.partnershipContract
                                                ?.status === "rejected"
                                            ? "text-danger"
                                            : "text-warning"
                                      }
                                    >
                                      {singlePartnerData?.uploadPartnerContractPDF
                                        ? singlePartnerData?.partnershipContract?.status
                                            ?.charAt(0)
                                            .toUpperCase() +
                                          singlePartnerData?.partnershipContract?.status?.slice(
                                            1,
                                          )
                                        : ""}
                                    </span>
                                  </h5>
                                  {singlePartnerData?.partnershipContract
                                    ?.status === "rejected" && (
                                    <p>
                                      <span className="cardTitle">
                                        Rejection message:
                                      </span>{" "}
                                      {
                                        singlePartnerData?.partnershipContract
                                          ?.rejectionReason
                                      }
                                    </p>
                                  )}
                                  <p className="cardDesc">
                                    {singlePartnerData?.uploadPartnerContractPDF
                                      ? "Your freight contract is now available. Read through the details and sign to proceed with your vendor contract."
                                      : "You can upload your freight contract here to proceed with the vendor contract."}
                                  </p>

                                  {(singlePartnerData?.partnershipContract
                                    ?.status === "" ||
                                    singlePartnerData?.partnershipContract
                                      ?.status === "pending") && (
                                    <>
                                      <div>
                                        <div
                                          className="lc__card lc__cardRow"
                                          style={{ minWidth: "700px" }}
                                        >
                                          <div className="quotation-upload-wrapper">
                                            <div className="quotation-upload-header">
                                              <label
                                                htmlFor="uploadQuotation"
                                                className="quotation-upload-label"
                                              >
                                                <div className="quotation-upload-icon">
                                                  <img
                                                    src="/assets/img/progressIconContainer.svg"
                                                    alt="Upload"
                                                  />
                                                </div>

                                                <div className="quotation-upload-text">
                                                  <div className="quotation-upload-title">
                                                    Contract (Upload pdf, jpg,
                                                    png)
                                                  </div>
                                                  <div className="quotation-upload-subtitle">
                                                    File format: png&nbsp;&nbsp;
                                                    File size: 5mb
                                                  </div>
                                                </div>
                                              </label>

                                              {singlePartnerData?.uploadPartnerContractPDF && (
                                                <span
                                                  className="lc__viewBtn_Admin"
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleViewClick();
                                                  }}
                                                >
                                                  View
                                                </span>
                                              )}
                                            </div>

                                            <div className="quotation-upload-progress">
                                              <div
                                                className={`quotation-upload-progress-bar ${
                                                  isContractUploaded
                                                    ? "uploaded"
                                                    : ""
                                                }`}
                                              />
                                            </div>

                                            <input
                                              id="uploadQuotation"
                                              type="file"
                                              name="file"
                                              ref={fileInputRef}
                                              style={{ display: "none" }}
                                              onChange={
                                                handleContractFileChange
                                              }
                                              disabled={
                                                singlePartnerData?.isAcceptedQuotation ===
                                                true
                                              }
                                              accept="image/*,application/pdf"
                                            />
                                          </div>
                                        </div>
                                      </div>

                                      <div className="submitquotationOuter submitContractOuter">
                                        <button
                                          className={`cancelQuotation  ${
                                            contractLoader
                                              ? "submitDisable"
                                              : ""
                                          }`}
                                          disabled={contractLoader}
                                          onClick={() => handleContractCancel()}
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          className={`submitQuotation ${contractLoader || contractLoading ? "submitDisable" : ""}`}
                                          disabled={contractLoader}
                                          onClick={handleContractSubmit}
                                        >
                                          {contractLoading ? (
                                            <>
                                              <span className="spinner" />{" "}
                                              Submitting...
                                            </>
                                          ) : (
                                            "Submit"
                                          )}
                                        </button>
                                      </div>
                                    </>
                                  )}

                                  {["accepted", "rejected"].includes(
                                    singlePartnerData?.partnershipContract
                                      ?.status,
                                  ) && (
                                    <button
                                      className="btn viewBtn mt-2"
                                      onClick={() => handleViewClick()}
                                    >
                                      View contract
                                    </button>
                                  )}
                                </div>

                                {/* <img
                                  src="/assets/img/contract.png"
                                  style={{
                                    width: "250px",
                                    height: "140px",
                                    borderRadius: "8px",
                                    objectFit: "cover",
                                  }}
                                /> */}
                              </div>
                            </>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="lc__actions">
                        <button
                          type="button"
                          className="lc__btn lc__btn--ghost"
                          onClick={handleOpenRejectModal}
                          disabled={
                            loadingAction === "accept" ||
                            loadingAction === "reject"
                          }
                        >
                          {loadingAction === "reject"
                            ? "Processing..."
                            : "Reject request"}
                        </button>

                        <button
                          type="button"
                          className="lc__btn lc__btn--primary"
                          onClick={handleOpenAcceptModal}
                          disabled={
                            loadingAction === "accept" ||
                            loadingAction === "reject"
                          }
                        >
                          {loadingAction === "accept"
                            ? "Processing..."
                            : "Accept request"}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* ✅ Confirmation Modal */}
                  <ConfirmationModal
                    show={confirmation.show}
                    title={
                      confirmation.type === "accept"
                        ? "Confirm acceptance"
                        : "Confirm rejection"
                    }
                    onConfirm={handleConfirmAction}
                    onCancel={handleCloseModal}
                    confirmText={
                      confirmation.type === "accept"
                        ? "Yes, accept"
                        : "Yes, reject"
                    }
                    cancelText="Cancel"
                  >
                    {confirmation.type === "accept" ? (
                      <p>
                        Are you sure you want to accept this vendor request?
                      </p>
                    ) : (
                      <>
                        <p>
                          Are you sure you want to reject this vendor request?
                        </p>
                        <textarea
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder="Enter reason for rejection..."
                          rows="4"
                          style={{
                            width: "100%",
                            marginTop: "10px",
                            padding: "8px",
                            borderRadius: "5px",
                            border: "1px solid #ccc",
                          }}
                        />
                      </>
                    )}
                  </ConfirmationModal>
                </section>
              </main>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
